import { 
  collection, 
  doc, 
  getDocs, 
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateAssignmentId, isNewIdFormat, convertDateIdToNewFormat } from './idGenerator';

/**
 * 과제 ID 마이그레이션 서비스
 * 기존 ID 형식을 새로운 통일된 형식으로 변환
 */
export class IdMigrationService {
  private result = {
    success: false,
    processedCount: 0,
    errorCount: 0,
    errors: [] as string[],
    duration: 0
  };

  /**
   * 전체 ID 마이그레이션 실행
   */
  async runIdMigration(userId: string, classId: string): Promise<void> {
    const startTime = Date.now();
    
    try {
      console.log('🚀 ID 마이그레이션 시작...');
      
      // 1. 일반 과제 ID 마이그레이션
      await this.migrateGeneralAssignments(userId, classId);
      
      // 2. 프로젝트 과제 ID 마이그레이션
      await this.migrateProjectAssignments(userId, classId);
      
      // 3. 루틴 과제 ID 마이그레이션
      await this.migrateRoutineAssignments(userId, classId);
      
      this.result.success = true;
      this.result.duration = Date.now() - startTime;
      
      console.log('✅ ID 마이그레이션 완료:', this.result);
    } catch (error) {
      console.error('❌ ID 마이그레이션 실패:', error);
      this.result.errors.push(String(error));
      this.result.duration = Date.now() - startTime;
    }
  }

  /**
   * 일반 과제 ID 마이그레이션
   */
  private async migrateGeneralAssignments(userId: string, classId: string): Promise<void> {
    console.log('📝 일반 과제 ID 마이그레이션 시작...');
    
    const assignmentsRef = collection(db, 'users', userId, 'classes', classId, 'generalAssignments');
    const assignmentsSnap = await getDocs(assignmentsRef);
    
    const batch = writeBatch(db);
    
    for (const assignmentDoc of assignmentsSnap.docs) {
      const oldId = assignmentDoc.id;
      
      // 이미 새로운 형식인 경우 스킵
      if (isNewIdFormat(oldId)) {
        continue;
      }
      
      const newId = generateAssignmentId('general');
      const assignmentData = assignmentDoc.data();
      
      // 새 ID로 문서 생성
      const newAssignmentRef = doc(db, 'users', userId, 'classes', classId, 'assignments', newId);
      batch.set(newAssignmentRef, { ...assignmentData, id: newId });
      
      // 기존 문서 삭제
      batch.delete(assignmentDoc.ref);
      
      // assignmentStatuses도 마이그레이션
      await this.migrateAssignmentStatuses(userId, classId, oldId, newId, batch);
      
      this.result.processedCount++;
      console.log(`✅ 일반 과제 ID 마이그레이션: ${oldId} → ${newId}`);
    }
    
    await batch.commit();
    console.log('✅ 일반 과제 ID 마이그레이션 완료');
  }

  /**
   * 프로젝트 과제 ID 마이그레이션
   */
  private async migrateProjectAssignments(userId: string, classId: string): Promise<void> {
    console.log('📝 프로젝트 과제 ID 마이그레이션 시작...');
    
    const projectsRef = collection(db, 'users', userId, 'classes', classId, 'projectAssignments');
    const projectsSnap = await getDocs(projectsRef);
    
    const batch = writeBatch(db);
    
    for (const projectDoc of projectsSnap.docs) {
      const oldId = projectDoc.id;
      
      // 이미 새로운 형식인 경우 스킵
      if (isNewIdFormat(oldId)) {
        continue;
      }
      
      const newId = generateAssignmentId('project');
      const projectData = projectDoc.data();
      
      // 하위과제는 기존 ID 유지 (순서 변경으로 인한 데이터 무결성 보장)
      const migratedSubTasks = projectData.subTasks || [];
      
      // 새 ID로 문서 생성
      const newProjectRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignments', newId);
      batch.set(newProjectRef, { 
        ...projectData, 
        id: newId,
        subTasks: migratedSubTasks
      });
      
      // 기존 문서 삭제
      batch.delete(projectDoc.ref);
      
      // 하위과제 상태는 기존 ID 유지 (데이터 무결성 보장)
      
      this.result.processedCount++;
      console.log(`✅ 프로젝트 과제 ID 마이그레이션: ${oldId} → ${newId}`);
    }
    
    await batch.commit();
    console.log('✅ 프로젝트 과제 ID 마이그레이션 완료');
  }

  /**
   * 루틴 과제 ID 마이그레이션
   */
  private async migrateRoutineAssignments(userId: string, classId: string): Promise<void> {
    console.log('📝 루틴 과제 ID 마이그레이션 시작...');
    
    const routinesRef = collection(db, 'users', userId, 'classes', classId, 'routineAssignments');
    const routinesSnap = await getDocs(routinesRef);
    
    const batch = writeBatch(db);
    
    for (const routineDoc of routinesSnap.docs) {
      const oldId = routineDoc.id;
      
      // 이미 새로운 형식인 경우 스킵
      if (isNewIdFormat(oldId)) {
        continue;
      }
      
      const newId = generateAssignmentId('routine');
      const routineData = routineDoc.data();
      
      // 새 ID로 문서 생성
      const newRoutineRef = doc(db, 'users', userId, 'classes', classId, 'routineAssignments', newId);
      batch.set(newRoutineRef, { ...routineData, id: newId });
      
      // 기존 문서 삭제
      batch.delete(routineDoc.ref);
      
      // 루틴 과제 날짜 상태는 datetask- 접두사 추가 (데이터 무결성 보장)
      await this.migrateRoutineDateIds(userId, classId, oldId, newId, batch);
      
      this.result.processedCount++;
      console.log(`✅ 루틴 과제 ID 마이그레이션: ${oldId} → ${newId}`);
    }
    
    await batch.commit();
    console.log('✅ 루틴 과제 ID 마이그레이션 완료');
  }

  /**
   * 일반 과제 상태 마이그레이션
   */
  private async migrateAssignmentStatuses(
    userId: string, 
    classId: string, 
    oldAssignmentId: string, 
    newAssignmentId: string, 
    batch: any
  ): Promise<void> {
    const oldStatusesRef = collection(db, 'users', userId, 'classes', classId, 'assignmentStatuses', oldAssignmentId, 'students');
    const oldStatusesSnap = await getDocs(oldStatusesRef);
    
    for (const statusDoc of oldStatusesSnap.docs) {
      const statusData = statusDoc.data();
      const newStatusRef = doc(db, 'users', userId, 'classes', classId, 'assignmentStatuses', newAssignmentId, 'students', statusDoc.id);
      batch.set(newStatusRef, { ...statusData, assignmentId: newAssignmentId });
      batch.delete(statusDoc.ref);
    }
  }


  /**
   * 루틴 과제 날짜 ID 마이그레이션 (datetask- 접두사 추가)
   */
  private async migrateRoutineDateIds(
    userId: string, 
    classId: string, 
    oldRoutineId: string, 
    newRoutineId: string, 
    batch: any
  ): Promise<void> {
    const oldStatusesRef = collection(db, 'users', userId, 'classes', classId, 'routineAssignmentStatuses', oldRoutineId, 'dates');
    const oldStatusesSnap = await getDocs(oldStatusesRef);
    
    for (const dateDoc of oldStatusesSnap.docs) {
      const oldDateId = dateDoc.id;
      const newDateId = convertDateIdToNewFormat(oldDateId);
      const dateData = dateDoc.data();
      
      // 새 ID로 문서 생성
      const newDateRef = doc(db, 'users', userId, 'classes', classId, 'routineAssignmentStatuses', newRoutineId, 'dates', newDateId);
      batch.set(newDateRef, { ...dateData, id: newDateId, routineAssignmentId: newRoutineId });
      
      // 기존 문서 삭제
      batch.delete(dateDoc.ref);
      
      console.log(`✅ 루틴 날짜 ID 마이그레이션: ${oldDateId} → ${newDateId}`);
    }
  }

  /**
   * 마이그레이션 결과 반환
   */
  getResult() {
    return this.result;
  }
}

export const idMigrationService = new IdMigrationService();
